import { ISchema } from "@formily/react";

export interface FormConfiguration {
  settings: FormConfigurationSettings;
  form: ISchema;
}

export interface FormConfigurationSettings {
  name?: string;
  endpoint: string;
  format: FormConfigurationData;
}

export interface FormConfigurationData {
  [key: string]: unknown;
}

export function resolveFormConfigurationTemplate(
  template: FormConfigurationData,
  form: Record<string, any>,
): any {
  const isExactPlaceholder = (str: string) => {
    // Match simple placeholders like {{propertyName}}
    const match = str.match(/^{{\s*([\w.[\]@]+)\s*}}$/);
    return match ? match[1] : null;
  };

  const castByType = (value: any, type: string) => {
    if (type === "int") return parseInt(value, 10);
    if (type === "float") return parseFloat(value);
    if (type === "boolean") return value === "true" || value === true;
    return String(value);
  };

  const interpolate = (value: any): any => {
    if (typeof value === "string") {
      const key = isExactPlaceholder(value);
      if (key) {
        // Use form.getValuesIn directly for exact placeholders
        const raw = form.getValuesIn(key);
        return raw === "" ? undefined : String(raw ?? "");
      }

      const interpolated = value.replace(
        /{{\s*([\w.[\]@]+)\s*}}/g,
        (_, path) => {
          // Use form.getValuesIn directly for interpolated placeholders
          const raw = form.getValuesIn(path);
          return raw != null ? String(raw) : "";
        },
      );

      return interpolated === "" ? undefined : interpolated;
    }

    if (Array.isArray(value)) {
      return value.map(interpolate).filter((v) => v !== undefined && v !== "");
    }

    if (typeof value === "object" && value !== null) {
      const keys = Object.keys(value);
      const isTypedValue =
        keys.length === 2 &&
        "value" in value &&
        "type" in value &&
        typeof value.value === "string" &&
        typeof value.type === "string";

      if (isTypedValue) {
        const key = isExactPlaceholder(value.value);
        if (key) {
          // Use form.getValuesIn directly for typed values
          const raw = form.getValuesIn(key);
          if (raw === "" || raw == null) return undefined;
          return castByType(raw, value.type);
        }
      }

      const result: Record<string, any> = {};
      for (const k in value) {
        const v = interpolate(value[k]);
        if (v !== undefined && v !== "") {
          result[k] = v;
        }
      }

      return Object.keys(result).length > 0 ? result : undefined;
    }

    return value;
  };

  return interpolate(template);
}
